import { fullLabel } from "../components/WhereItIs";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProblemMove } from "@/lib/useProblemMove";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { FloorActionButton } from "@/floor/FloorActionButton";
import { useFlash } from "@/floor/useFlash";

import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Package, Loader2, ChevronDown, StickyNote } from "lucide-react";
import { AuthImg } from "@/components/AuthImg";

// FLOOR problem-move resolver — full context + scan-first fixes, resolved on the spot.
// Same big-touch standard as the rest of the Floor surface (24px+ targets, large type,
// beep + flash). No screen-bouncing: the fix actions live right here.
export default function FloorProblemCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { detail, loading, busy, doFix, doResolve } = useProblemMove(id);
  const { trigger, FlashOverlay } = useFlash();
  const [scanOpen, setScanOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const done = (msg) => { trigger("success", "FIXED"); toast.success(msg); setTimeout(() => navigate("/floor"), 550); };
  const fail = (msg) => { trigger("error", "TRY AGAIN"); toast.error(msg); };

  const move = async (code) => {
    try {
      const r = await doFix("move_to", code);
      if (r.ok) { setScanOpen(false); done(r.message || "Moved & resolved."); } else fail(r.message || "Move failed — flag kept open.");
    } catch (e) { fail(e?.response?.data?.detail || "Move failed."); }
  };
  const sendBack = async () => {
    try { const r = await doFix("send_back"); r.ok ? done(r.message || "Sent back & resolved.") : fail(r.message || "Failed."); }
    catch (e) { fail(e?.response?.data?.detail || "Failed."); }
  };
  const confirmEmpty = async () => {
    try { const r = await doFix("confirm_empty"); r.ok ? done("Confirmed empty & resolved.") : fail("Failed."); }
    catch (e) { fail(e?.response?.data?.detail || "Failed."); }
  };
  const falseAlarm = async () => {
    try { await doResolve("false alarm — verified correct on the floor"); done("Resolved (false alarm)."); }
    catch { fail("Couldn't resolve."); }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="w-10 h-10 animate-spin" /></div>;
  if (!detail) return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4" data-testid="floor-problem-missing">
      <div className="text-3xl font-black">Not found</div>
      <button onClick={() => navigate("/floor")} className="text-[#B01E28] font-bold text-xl">Back to scan</button>
    </div>
  );
  if (detail.resolved) return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4" data-testid="floor-problem-resolved">
      <div className="text-3xl font-black text-emerald-600">Already resolved</div>
      <button onClick={() => navigate("/floor")} className="text-[#B01E28] font-bold text-xl">Back to scan</button>
    </div>
  );

  const isZero = detail.kind === "zero_parts";
  const parts = detail.parts_in_tote || [];
  const shown = showAll ? parts : parts.slice(0, 5);
  const from = detail.from_location, to = detail.attempted_destination;

  return (
    <div className="flex-1 flex flex-col p-4 sm:p-6 gap-4 overflow-y-auto" data-testid="floor-problem-card">
      {FlashOverlay}

      {/* Header */}
      <div className="flex items-start gap-3">
        <span className={`rounded-lg text-sm font-black uppercase px-3 py-1.5 shrink-0 ${isZero ? "bg-amber-200 text-amber-900" : "bg-red-200 text-red-900"}`}>
          {isZero ? "0 parts" : "Move error"}
        </span>
        <div className="min-w-0">
          <div className="text-3xl font-black tracking-tight truncate" data-testid="floor-problem-tote">{detail.tote?.short_code || detail.container_code || "?"}</div>
          <div className="text-lg text-slate-600 font-medium">{detail.reason}</div>
        </div>
      </div>

      {/* FROM -> attempted TO */}
      <div className="rounded-2xl border-2 border-[#E2E8F0] bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Move</div>
        <div className="flex items-center gap-3 text-lg font-bold flex-wrap">
          <span className="text-slate-900">{fullLabel(from) || from?.short_code || "—"}</span>
          <ArrowRight className="w-6 h-6 text-slate-400 shrink-0" />
          <span className="text-slate-900">{fullLabel(to) || to?.short_code || detail.dest_code || detail.location_scan || "—"}</span>
        </div>
        <div className="mt-2 text-base text-slate-500">
          Tote is now at <b className="text-slate-800">{fullLabel(detail.tote?.current_location) || detail.tote?.current_location?.short_code || "—"}</b>
        </div>
        {(detail.container_scan || detail.location_scan) && (
          <div className="mt-2 text-sm font-mono text-slate-400 truncate">scanned: {detail.container_scan || "?"} → {detail.location_scan || "?"}</div>
        )}
        <div className="mt-1 text-sm text-slate-400">{detail.by} · {detail.at ? new Date(detail.at).toLocaleString() : ""}</div>
      </div>

      {/* Parts in tote */}
      <div className="rounded-2xl border-2 border-[#E2E8F0] bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">In the tote now · {detail.part_count} part{detail.part_count === 1 ? "" : "s"}</div>
        {parts.length === 0 ? (
          <div className="text-base text-slate-500">The tote is empty right now.</div>
        ) : (
          <>
            <div className="space-y-2">
              {shown.map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                    {p.photo ? <AuthImg fileId={p.photo} thumb alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-slate-400" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-slate-900 truncate">{p.short_code}</div>
                    <div className="text-sm text-slate-500 truncate">{p.name || "—"}</div>
                  </div>
                </div>
              ))}
            </div>
            {parts.length > 5 && (
              <button onClick={() => setShowAll((s) => !s)} data-testid="floor-problem-parts-more"
                className="mt-2 text-[#B01E28] font-bold inline-flex items-center gap-1">
                {showAll ? "Show less" : `Show all ${parts.length}`}<ChevronDown className={`w-4 h-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
              </button>
            )}
          </>
        )}
      </div>

      {/* Lot(s) + dismantler notes */}
      {(detail.lots || []).length > 0 && (
        <div className="rounded-2xl border-2 border-[#E2E8F0] bg-white p-4" data-testid="floor-problem-lots">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Lot(s) & dismantler notes</div>
          <div className="space-y-3">
            {detail.lots.map((l) => (
              <div key={l.id}>
                <div className="font-bold text-slate-900">{l.batch_number || l.label || "Lot"}{l.donor_label ? ` · ${l.donor_label}` : ""}</div>
                {l.notes
                  ? <div className="mt-1 text-base text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 max-h-48 overflow-y-auto"><StickyNote className="w-4 h-4 inline mr-1.5 text-amber-500" />{l.notes}</div>
                  : <div className="mt-1 text-sm text-slate-400">No dismantler notes on this lot.</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ACTIONS — fix it right here */}
      <div className="grid gap-3 pt-1">
        <FloorActionButton testid="floor-problem-move" tone="primary" icon="ArrowRightLeft" busy={busy}
          label="Move to correct bin" onClick={() => setScanOpen(true)} />
        {detail.can_send_back && (
          <FloorActionButton testid="floor-problem-sendback" icon="MapPin" busy={busy}
            label={`Send back to ${detail.from_location?.short_code || "origin"}`} onClick={sendBack} />
        )}
        <FloorActionButton testid="floor-problem-open-tote" icon="Boxes" busy={busy}
          label="Open the tote" onClick={() => detail.tote?.id && navigate(`/floor/location/${detail.tote.id}`)} disabled={!detail.tote?.id} />
        {isZero && (
          <FloorActionButton testid="floor-problem-confirm-empty" icon="PackageOpen" busy={busy}
            label="Confirm empty — resolve" onClick={confirmEmpty} />
        )}
        <button type="button" data-testid="floor-problem-false-alarm" disabled={busy} onClick={falseAlarm}
          className="h-14 rounded-xl border-2 border-[#E2E8F0] text-slate-500 font-bold text-lg active:scale-95 transition-transform disabled:opacity-50">
          False alarm — resolve
        </button>
      </div>

      <FloorScanOverlay open={scanOpen} testid="floor-problem-scan"
        title="Scan the correct bin" sub={`Tote ${detail.tote?.short_code || ""}`} submitLabel="Move here"
        onScan={move} onClose={() => setScanOpen(false)} busy={busy} />
    </div>
  );
}
